import { Link } from 'react-router-dom';
import { MailIcon, PlusIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { EmptyState } from '../ui/EmptyState';
import { useNewsletters } from './useNewsletters';
import { issueState, issueStateLabel, issueStateTone } from './newsletterRules';
import { btnPrimary, rowLink } from '../ui/styles';
import { fromIso, longDate } from '../../core/format/dates';
import { euro } from '../../core/format/currency';
import type { NewsletterIssue } from '../../core/types/newsletter';

/** Van samenstellen tot verstuurd. Volgorde telt, dus genummerd. */
const steps = [{
  title: 'Stel samen',
  text: 'Kies een ontwerp als kop, schrijf je tekst en zet er producten of een artikel bij.'
}, {
  title: 'Kies een doelgroep en sla op',
  text: 'De server verstuurt altijd de opgeslagen versie.'
}, {
  title: 'Stuur een testmail',
  text: 'Bekijk hem op je eigen telefoon voordat iedereen hem krijgt.'
}, {
  title: 'Verstuur of plan in',
  text: 'Daarna zie je hier hoeveel lezers hem openden en wat hij opleverde.'
}];
const pct = (part: number, whole: number) => whole ? `${Math.round(part / whole * 100)}%` : '–';

/** Wanneer: verstuurd, ingepland of de eigen verzenddag. */
function when(n: NewsletterIssue) {
  if (n.delivery?.sentAt) return `Verstuurd ${longDate(fromIso(n.delivery.sentAt.slice(0, 10)))}`;
  if (n.delivery?.scheduledAt) return `Gaat ${longDate(fromIso(n.delivery.scheduledAt.slice(0, 10)))} om ${n.delivery.scheduledAt.slice(11, 16)}`;
  return n.sendDate ? `Gepland op ${longDate(fromIso(n.sendDate))}` : 'Nog niet gepland';
}

/** Tabblad Nieuwsbrief in Mail & nieuwsbrief: in voorbereiding, en wat verstuurd is met de resultaten. */
export function Newsletters() {
  const {
    issues,
    createIssue
  } = useNewsletters();
  const open = issues.filter(n => issueState(n) !== 'verzonden').sort((a, b) => (a.sendDate || '9').localeCompare(b.sendDate || '9'));
  const sent = issues.filter(n => issueState(n) === 'verzonden').sort((a, b) => (b.delivery?.sentAt ?? b.sendDate).localeCompare(a.delivery?.sentAt ?? a.sendDate));
  const list = (items: NewsletterIssue[]) => <ul className="divide-y divide-line" data-id="element-3412" data-mp="3i91d5mob3q5u">
      {items.map(n => {
      const state = issueState(n);
      const stats = n.delivery?.stats;
      return <li key={n.id} data-id="element-3413" data-mp="3r2607goo0mq8">
            <Link to={`/be/mail/nieuwsbrief/${n.id}`} className={rowLink} data-id="element-3414" data-mp="3r2o8jbolqrei">
              <span className="min-w-0 flex-1" data-id="element-3415" data-mp="2uei4oqzi831a">
                <span className="block truncate text-[14px] text-ink" data-id="element-3416" data-mp="2zc9yjqjplz84">{n.subject || 'Zonder onderwerp'}</span>
                <span className="block truncate text-[13px] text-muted" data-id="element-3417" data-mp="33e4wcl2fluh5">
                  {when(n)}
                  {n.delivery ? ` · ${n.delivery.segmentName}` : ''}
                </span>
                {stats && stats.recipients > 0 && <span className="mt-1 block text-[12px] tabular-nums text-muted" data-id="element-3418" data-mp="in4eiag3x4ye">
                    {stats.recipients} ontvangers · {pct(stats.opened, stats.recipients)} geopend · {pct(stats.clicked, stats.recipients)} geklikt
                    {stats.revenueCents > 0 ? ` · ${euro(stats.revenueCents / 100)} omzet` : ''}
                  </span>}
              </span>
              <Badge tone={issueStateTone[state]} data-id="element-3419" data-mp="nzjllrvmndgx">{issueStateLabel[state]}</Badge>
            </Link>
          </li>;
    })}
    </ul>;
  return <div className="grid gap-6 lg:grid-cols-3" data-id="element-3420" data-mp="ezwjt4yzk5nc">
      <div className="min-w-0 space-y-6 lg:col-span-2" data-id="element-3421" data-mp="f1r6anpwo5tj">
        <div className="flex justify-end" data-id="element-3422" data-mp="a1czf901j1x3">
          <button type="button" onClick={() => createIssue()} className={btnPrimary} data-id="element-3423" data-mp="9ziazkpk8ro9">
            <PlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3424" data-mp="52rvayho3y3d" />
            Nieuwe nieuwsbrief
          </button>
        </div>
        {issues.length === 0 ? <EmptyState icon={MailIcon} title="Nog geen nieuwsbrieven" text="Eén keer per maand, over huid en seizoen. Begin met een ontwerp als kop en een persoonlijk stuk tekst." data-id="element-3425" data-mp="1zgb08te12iyp" /> : <>
            {open.length > 0 && <Panel flush title="In voorbereiding" actions={<span className="text-[13px] tabular-nums text-muted" data-id="element-3427" data-mp="27fxt4tx436e5">{open.length}</span>} data-id="element-3426" data-mp="27h8ukn334223">
                {list(open)}
              </Panel>}
            {sent.length > 0 && <Panel flush title="Verstuurd" actions={<span className="text-[13px] tabular-nums text-muted" data-id="element-3429" data-mp="1jr4sik5joad7">{sent.length}</span>} data-id="element-3428" data-mp="1xgyg6z5bcolc">
                {list(sent)}
              </Panel>}
          </>}
      </div>

      <aside className="min-w-0 space-y-6" aria-label="Werkwijze" data-id="element-3430" data-mp="1ojo6vjc239j2">
        <Panel title="Zo verstuur je hem" data-id="element-3431" data-mp="19ujnhytiyz9d">
          <ol className="space-y-4" data-id="element-3432" data-mp="1es1on31awa6t">
            {steps.map((s, i) => <li key={s.title} className="flex gap-3" data-id="element-3433" data-mp="1epyzel7z2l35" data-mpi={i}>
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sand text-[12px] tabular-nums text-ink" aria-hidden="true" data-id="element-3434" data-mp="10tbp7gr0dyfa">
                  {i + 1}
                </span>
                <span data-id="element-3435" data-mp="m6muds7pc9f8">
                  <span className="block text-[14px] text-ink" data-id="element-3436" data-mp="m5trx3fs062w">{s.title}</span>
                  <span className="block text-[13px] leading-snug text-muted" data-id="element-3437" data-mp="h7s9mkq6cash">{s.text}</span>
                </span>
              </li>)}
          </ol>
          <p className="mt-4 border-t border-line pt-4 text-[12px] leading-relaxed text-muted" data-id="element-3438" data-mp="r4wmtv21kvx5">
            Liever zelf in Brevo? In elke nieuwsbrief staat “Kopieer voor Brevo”. Hoeveel lezers je hebt, zie je bij{' '}
            <Link to="/be/mail?tab=abonnees" className="text-ink underline underline-offset-2" data-id="element-3439" data-mp="1064kqc0nbuqp">
              Abonnees & doelgroepen
            </Link>
            .
          </p>
        </Panel>
      </aside>
    </div>;
}